import React, { useState } from 'react';
import { Phone, Mail, MapPin, User, MessageSquare, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { companyInfo, features } from '../data/mock';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);
    setSubmitted(true);
    toast.success('Request submitted! We\'ll contact you soon for your free estimate.');
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', message: '' });
      setSubmitted(false);
    }, 3000);
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Get Your <span className="text-red-600">Free Estimate</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Contact us today and let's discuss your roofing project
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-black mb-6">Contact Information</h3>
            
            <div className="space-y-6 mb-8">
              <a 
                href={`tel:${companyInfo.phone.replace(/\D/g, '')}`}
                className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
              >
                <div className="bg-red-600 p-3 rounded-lg group-hover:bg-red-700 transition-colors">
                  <Phone className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">Phone</h4>
                  <p className="text-gray-600">{companyInfo.phone}</p>
                  <p className="text-sm text-red-600 font-medium">24/7 Emergency Service</p>
                </div>
              </a>

              <a 
                href={`mailto:${companyInfo.email}`}
                className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
              >
                <div className="bg-red-600 p-3 rounded-lg group-hover:bg-red-700 transition-colors">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">Email</h4>
                  <p className="text-gray-600">{companyInfo.email}</p>
                </div>
              </a>

              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="bg-red-600 p-3 rounded-lg">
                  <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">Address</h4>
                  <p className="text-gray-600">{companyInfo.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="bg-red-600 p-3 rounded-lg">
                  <User className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-black mb-1">Manager</h4>
                  <p className="text-gray-600">{companyInfo.manager}</p>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-black text-white p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-4">Why Choose Expert Roofing?</h3>
              <div className="space-y-3">
                {features.map((feature) => (
                  <div key={feature.id} className="flex items-start gap-3">
                    <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold">{feature.title}</h4>
                      <p className="text-sm text-gray-300">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="text-green-600" size={40} />
                    </div>
                    <h3 className="text-2xl font-bold text-black mb-2">Thank You!</h3>
                    <p className="text-gray-600">We'll contact you soon for your free estimate.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-black mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-black mb-2">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-black mb-2">
                        Phone *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(425) 555-0123"
                        className="border-gray-300 focus:border-red-600 focus:ring-red-600"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-black mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your roofing project..."
                        rows={5}
                        className="border-gray-300 focus:border-red-600 focus:ring-red-600 resize-none"
                      />
                    </div>

                    <Button 
                      type="submit"
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold"
                    >
                      <Send className="mr-2" size={20} />
                      Request Free Estimate
                    </Button>

                    <p className="text-sm text-gray-600 text-center">
                      We'll respond within 24 hours
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
